import React, { useContext, useState, useEffect } from 'react';
import classnames from 'classnames';
import AC_GRID from '../../AutocodeTypes';
import ACDictionaryContext from '../../ACContext/ACDictionaryContext';
import { useACSelectionBoxContext } from '../../ACContext/ACSelectionBoxContext';
import AutocodeSelectionAction from '../../AutocodeSelectionBox/AutocodeSelectionActions';
import { AC_ROOT_NODE_ID } from '../../AutocodeConstants';
import AutocodeCheckBox from '../../ACShared/AutocodeCheckBox';
import GenericButton from '../../../common/GenericButton';
import AutocodeDropdownV2 from '../../ACShared/AutocodeDropdownV2';
import ACTemplateRow from './ACTemplateRow';
import AutocodeCustomDragLayer from '../../AutocodeCustomDragLayer';
import { getDatapointsForTemplate1Category } from '../../../../util/AutocodeDragAndDropUtils';

const ACTemplate1Category = () => {
  const { selectedNodeAnswers, disableAddButton } = useContext(
    ACDictionaryContext
  );
  const acSelContext = useACSelectionBoxContext();
  const { insertLeafNode } = AutocodeSelectionAction(
    acSelContext.state,
    acSelContext.dispatch
  );

  /*
    expected data shape in selectedNodeAnswers.root:
    - Category
      - Most Recent Time Period (ex: ... last 30 days)
        - Volume
          - Heavy
          - Medium
          - Light
          - 0, 1, 2, 3, etc...
          - Total (doublebase only)
      - Full Time Period (ex: ... last 6 months)
        - Users
          - Total
        - Sole
          - Brand
          - Type
          - Kind
          - Form
  */

  const volumeItems = selectedNodeAnswers.root.children.find(c =>
    c.children.some(cc => cc.shortText === 'Volume')
  );

  // Volume items that are numbers (0 -> 9)
  // Not Heavy, Medium, Light, Total
  const volumeNumbers = volumeItems.children[0].children.filter(
    c =>
      !c.shortText.includes('Total') &&
      !c.shortText.includes('Heavy') &&
      !c.shortText.includes('Medium') &&
      !c.shortText.includes('Light')
  );

  const totalItem = volumeItems.children[0].children.find(
    c => c.shortText === 'Total'
  );

  const soleUsersItems = selectedNodeAnswers.root.children.find(c =>
    c.children.some(cc => cc.shortText === 'Sole' || cc.shortText === 'Users')
  );

  const usersItems = soleUsersItems.children.find(c => c.shortText === 'Users');

  const soleItems = soleUsersItems.children.find(c => c.shortText === 'Sole');

  const totalUsersItem = usersItems.children[0];

  // drop down items
  const userOptions = [
    {
      value: totalUsersItem.keyword,
      label: `${totalUsersItem.shortText} ${usersItems.shortText}`,
    },
    ...soleItems.children.map(i => ({
      value: i.keyword,
      label: `${soleItems.shortText} ${i.shortText}`,
    })),
  ];

  // selected drop down item
  // default selected is Total Users
  const [selectedUserOption, setSelectedUserOption] = useState(
    userOptions.find(o => o.label.includes('Total Users')).value
  );

  // disable add button flag
  const [addButtonDisabled, setaddButtonDisabled] = useState(true);

  // all checkbox selected
  const [allSelected, setAllSelected] = useState(false);

  // total and heavy medium light volume items and two range items
  // this assumes the volume data is the first three items in the children
  // of the second node under root
  const [selectedItems, setSelectedItems] = useState([]);

  // different types of items in the list
  const itemType = {
    TOTAL_USERS: 1,
    RECENT_USERS: 2,
    HML: 3,
    FROM_TO: 4,
    EXACTLY: 5,
    PRE_DEFINED_RANGE: 6,
  };

  useEffect(() => {
    setaddButtonDisabled(!selectedItems.some(i => i.checked));
    setAllSelected(selectedItems.every(i => i.checked));
  }, [selectedItems]);

  useEffect(() => {
    const parentPathId = volumeItems.children[0].pathId;
    const parentTextPath = volumeItems.children[0].textPath;
    // const timePeriod = getTimePeriodFromShortText(volumeItems.shortText);
    const timePeriod = volumeItems.shortText;
    // const timePeriodLabel = `${TEMPLATE_1_TIME_PERIOD_LABEL_PREFIX} ${timePeriod}`;
    const timePeriodLabel = timePeriod;
    // const fullTimePeriod = getTimePeriodFromShortText(soleUsersItems.shortText);
    const fullTimePeriod = soleUsersItems.shortText;
    // const fullTimePeriodLabel = `${TEMPLATE_1_TIME_PERIOD_LABEL_PREFIX} ${fullTimePeriod}`;
    const fullTimePeriodLabel = `users ${fullTimePeriod}`;

    setSelectedItems([
      ...(totalUsersItem
        ? [
            // full time period
            {
              ...totalUsersItem,
              type: itemType.TOTAL_USERS,
              datatype: 'N',
              addedText: fullTimePeriodLabel,
              checked: false,
              rangeItem: false,
            },
          ]
        : []),
      // Recent users item
      {
        type: itemType.RECENT_USERS,
        node: 'recentUsers',
        from: '1',
        datatype: 'N',
        shortText: 'Users', // Overwrite item label
        // addedText: `in ${timePeriod}`,
        addedText: timePeriod,
        pathId: `${parentPathId}.recent-users`, // made up path id
        textPath: `${parentTextPath}.recent-users`, // made up text path
        checked: false,
        rangeItem: false,
      },
      // Heavy, medium, light are first 3 volume items
      ...volumeItems.children[0].children.slice(0, 3).map(a => ({
        ...a,
        type: itemType.HML,
        addedText: timePeriodLabel,
        checked: false,
        rangeItem: false,
      })),
      // 'from to' and 'exactly' range items
      // only show if totalItem exists (double base studies)
      // otherwise show pre-defined ranges
      ...(totalItem
        ? [
            {
              type: itemType.FROM_TO,
              node: 'fromTo',
              datatype: 'B',
              from: '0',
              to: 'MAX',
              pathId: `${parentPathId}.fromTo`, // made up path id
              textPath: `${parentTextPath}.fromTo`, // made up text path
              keyword: 'volume',
              shortText: 'Category',
              checked: false,
              rangeItem: true,
              addedText: timePeriodLabel,
            },
            {
              type: itemType.EXACTLY,
              node: 'from',
              datatype: 'B',
              from: '0',
              pathId: `${parentPathId}.from`, // made up path id
              textPath: `${parentTextPath}.from`, // made up text path
              keyword: 'volume',
              shortText: 'Category',
              checked: false,
              rangeItem: true,
              addedText: timePeriodLabel,
            },
          ]
        : volumeNumbers.map(n => ({
            ...n,
            type: itemType.PRE_DEFINED_RANGE,
            checked: false,
            addedText: timePeriodLabel,
            rangeItem: false,
          }))),
    ]);
  }, [selectedNodeAnswers]);

  const selectAll = isChecked => {
    setSelectedItems(selectedItems.map(a => ({ ...a, checked: isChecked })));
    setAllSelected(isChecked);
  };

  const setItemList = (selectedItems, isChecked, item) => {
    const index = selectedItems.findIndex(a => a.node === item.node);
    setSelectedItems([
      ...selectedItems.slice(0, index),
      { ...selectedItems[index], checked: isChecked },
      ...selectedItems.slice(index + 1),
    ]);
  };

  const selectItem = (isChecked, item, event) => {
    let checkedItems = [];
    checkedItems = selectedItems.filter(i => i.checked);

    // check single item if shift key is not pressed or item is being unchecked
    // ...or shift key is pressed and no other items checked
    if (
      !event ||
      !event.nativeEvent.shiftKey ||
      (event && event.nativeEvent.shiftKey && checkedItems.length === 0) ||
      !isChecked
    ) {
      setItemList(selectedItems, isChecked, item);
      return;
    }
    // find range of items between last checked item and item being checked

    let lastCheckedItem = [];
    lastCheckedItem = checkedItems[checkedItems.length - 1];
    const startIndex = selectedItems.indexOf(lastCheckedItem);
    const endIndex = selectedItems.indexOf(item);
    let fromIndex = 0;
    let toIndex = 0;
    if (startIndex !== -1 && endIndex !== -1 && startIndex < endIndex) {
      fromIndex = startIndex;
      toIndex = endIndex;
    } else {
      fromIndex = endIndex;
      toIndex = startIndex;
    }

    // check all those items in range
    let itemsInRange = [];
    itemsInRange = selectedItems.slice(fromIndex, toIndex + 1);
    setSelectedItems([
      ...selectedItems.slice(0, fromIndex),
      ...itemsInRange.map(a => ({ ...a, checked: isChecked })),
      ...selectedItems.slice(toIndex + 1),
    ]);
  };

  const updateRangeItem = (fromTo, item) => {
    const index = selectedItems.findIndex(a => a.node === item.node);
    const updatedItem =
      fromTo.length === 1
        ? { ...selectedItems[index], from: fromTo[0] }
        : {
            ...selectedItems[index],
            from: fromTo[0],
            to: fromTo[1],
          };
    setSelectedItems([
      ...selectedItems.slice(0, index),
      updatedItem,
      ...selectedItems.slice(index + 1),
    ]);
  };
  const addToSelectionBox = () => {
    // Setting final datapoints data from AutocodeDragAndDropUtils to add data to selection box
    const datapoints = getDatapointsForTemplate1Category(
      selectedItems,
      selectedNodeAnswers,
      userOptions,
      selectedUserOption,
      totalUsersItem
    );
    // insert into selection box
    insertLeafNode(datapoints, AC_ROOT_NODE_ID, -1);

    // uncheck items after adding to selection box
    setSelectedItems(selectedItems.map(a => ({ ...a, checked: false })));
    setAllSelected(false);
  };

  const resetSelectedRows = () => {
    setAllSelected(false);
    setSelectedItems(selectedItems.map(a => ({ ...a, checked: false })));
  };

  const inputStyles = {
    background: '#F8FBFF',
    border: '1px solid #94A2B7',
    boxSizing: 'border-box',
    borderRadius: '3px',
    width: '39px',
    margin: '0 12px',
    display: 'inline',
  };

  // render a range input with one or two number inputs
  const renderRangeInput = rangeItem => {
    if (rangeItem.node === 'fromTo') {
      return (
        <span>
          From
          <input
            style={inputStyles}
            type="number"
            min="0"
            value={rangeItem.from}
            onChange={e =>
              updateRangeItem([e.target.value, rangeItem.to], rangeItem)
            }
          />
          to
          <input
            style={inputStyles}
            type="text"
            value={rangeItem.to}
            onChange={e =>
              updateRangeItem([rangeItem.from, e.target.value], rangeItem)
            }
          />
          {rangeItem.addedText}
        </span>
      );
    }
    return (
      <span>
        Exactly
        <input
          style={inputStyles}
          type="number"
          min="0"
          value={rangeItem.from}
          onChange={e => updateRangeItem([e.target.value], rangeItem)}
        />
        {rangeItem.addedText}
      </span>
    );
  };

  const getSelectedEntries = () => {
    const selectedDragableItem = selectedItems.filter(item => item.checked);
    selectedDragableItem.map(item => {
      item.dragText = item.rangeItem
        ? renderRangeInput(item)
        : `${item.shortText} ${item.addedText}`;
      return item;
    });
    return selectedDragableItem;
  };

  return (
    <div>
      <div className="ac-dictionary-container">
        <div className="ac-dictionary-dropdown">
          <AutocodeDropdownV2
            options={userOptions}
            selected={selectedUserOption}
            changeHandler={setSelectedUserOption}
          />
        </div>

        <div
          className={classnames('ac-dictionary-row', {
            'ac-dictionary-row-checked': allSelected,
          })}
        >
          <div className="ac-dictionary-row-checkbox">
            <AutocodeCheckBox
              onChange={selectAll}
              id="all-items"
              isChecked={allSelected}
              fill="#94A2B7"
              stroke="#94A2B7"
              allCheck
              label="All"
            />
          </div>
        </div>
        <div className="ac-dictionary-rows-list">
          <AutocodeCustomDragLayer
            items={getSelectedEntries()}
            type={AC_GRID.AC_TEMPLATE_1_CATEGORY}
          />
          {selectedItems.map(item => (
            <ACTemplateRow
              ACTemplateType={AC_GRID.AC_TEMPLATE_1_CATEGORY}
              key={item.node}
              onChange={selectItem}
              id={`item-${item.node}`}
              isChecked={item.checked}
              fill={item.checked ? '#94A2B7' : '#94A2B7'}
              stroke={item.checked ? '#94A2B7' : '#94A2B7'}
              data={item}
              label={
                item.rangeItem
                  ? renderRangeInput(item)
                  : `${item.shortText} ${item.addedText}`
              }
              selectedItems={selectedItems}
              selectedNodeAnswers={selectedNodeAnswers}
              isDragable={!addButtonDisabled && !disableAddButton}
              resetSelectedRows={resetSelectedRows}
              userOptions={userOptions}
              selectedUserOption={selectedUserOption}
              totalUsersItem={totalUsersItem}
            />
          ))}
        </div>
      </div>
      <div
        style={{
          borderTop: '1px solid #e0e0e0',
        }}
      >
        <div style={{ marginTop: '15px' }}>
          <GenericButton
            name="Add"
            btnStyle="ac-dictionary__addbutton"
            btnDisabled={addButtonDisabled || disableAddButton}
            onBtnClick={addToSelectionBox}
          />
        </div>
      </div>
    </div>
  );
};

export default ACTemplate1Category;
